import type { AxiosInstance } from "axios";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { apiErrorMessage } from "../lib/api.js";
import { cn } from "../lib/cn.js";
import {
  cellText,
  columnBands,
  columnTotal,
  fetchPeriodBreakdown,
  fetchReport,
  hasUnattributed,
  type PeriodRow,
  type ReportColumn,
  type ReportDefinition,
  type ReportQuery,
  type ReportRow,
  type Unattributed,
} from "../lib/reports.js";
import { sourceMark } from "../lib/source-marks.js";
import { Skeleton } from "./ui/skeleton.js";
import { TableResizeHandle, useTableResize } from "./TableResizeHandle.js";

/** Entities (sources / campaigns / ad sets / ads) fetched per request while the merchant scrolls down. */
export const ENTITY_CHUNK_SIZE = 4;

/** Rows of a block visible before it scrolls inside itself. */
const BLOCK_ROW_HEIGHT = 36;
const BLOCK_VISIBLE_ROWS = 4;
const BLOCK_MAX_HEIGHT = BLOCK_ROW_HEIGHT * BLOCK_VISIBLE_ROWS;

/** At most this many period requests in flight — each is a ClickHouse query. */
const MAX_PERIOD_REQUESTS = 2;

const LABEL_COLUMN_WIDTH = 300;
const DEFAULT_COLUMN_WIDTH = 140;

/** What decides which entities exist: everything the consolidated request sends except the paging. */
export type BreakdownFilters = Omit<ReportQuery, "from" | "to" | "limit" | "offset">;

interface Block {
  readonly periods: readonly PeriodRow[];
  /** The first chunk is on its way. */
  readonly loading: boolean;
  /** A later chunk is on its way. */
  readonly loadingMore: boolean;
  readonly hasMore: boolean;
  readonly failed: boolean;
}

const EMPTY_BLOCK: Block = { periods: [], loading: false, loadingMore: false, hasMore: true, failed: false };

/**
 * The Day / Week view of an attribution report: the same entities as the consolidated table, each one a "Grand Total" line with
 * its days (or weeks) under it, and "Overall Totals" at the foot — the landing-page trend's layout and its two-way lazy loading.
 *
 *   DOWN  — entities arrive `ENTITY_CHUNK_SIZE` at a time as the merchant scrolls.
 *   INSIDE — each entity's periods arrive `PERIOD_CHUNK_SIZE` at a time as its block is scrolled.
 *
 * Only entities that scrolled into view ask for their periods, and several share one request. The component holds no filter
 * state: the screen remounts it (a `key`) whenever the range, the filters or the mode change, so a response for the old question
 * can never land in the new table.
 */
export function AttributionBreakdownTable({
  client,
  report,
  columns,
  mode,
  from,
  to,
  filters,
}: {
  readonly client: AxiosInstance;
  readonly report: ReportDefinition;
  /** The report's visible columns (the model picker's choice); the first is the entity's name. */
  readonly columns: readonly ReportColumn[];
  readonly mode: "day" | "week";
  readonly from: string;
  readonly to: string;
  readonly filters: BreakdownFilters;
}): React.JSX.Element {
  const target = report.periodBreakdown;
  const entityKey = target?.entityKey ?? "";
  const labelKey = target?.labelKey ?? "";

  const nameColumn = columns[0];
  const metricColumns = useMemo(
    () => columns.slice(1).filter((column) => column.kind !== "text" && column.kind !== "status" && column.kind !== "week"),
    [columns],
  );
  const bands = useMemo(() => columnBands(metricColumns), [metricColumns]);
  const bandStarts = useMemo(() => {
    const starts = new Set<number>();
    let at = 0;
    for (const band of bands) {
      starts.add(at);
      at += band.span;
    }
    return starts;
  }, [bands]);

  const initialWidths = useMemo(
    () => [LABEL_COLUMN_WIDTH, ...metricColumns.map((column) => column.width ?? DEFAULT_COLUMN_WIDTH)],
    [metricColumns],
  );
  const { colWidths, startResize } = useTableResize(initialWidths, 80);
  const widthAt = (index: number): number => colWidths[index] ?? initialWidths[index] ?? DEFAULT_COLUMN_WIDTH;
  const metricWidths = metricColumns.map((_, index) => widthAt(index + 1));
  const totalWidth = widthAt(0) + metricWidths.reduce((a, b) => a + b, 0);
  const metricTotalWidth = totalWidth - widthAt(0);

  // ---- DOWN: the entities ----
  const [entities, setEntities] = useState<readonly ReportRow[]>([]);
  const [totals, setTotals] = useState<Readonly<Record<string, number>> | null>(null);
  const [unattributed, setUnattributed] = useState<Unattributed | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadingMoreRef = useRef(false);
  const hasMoreRef = useRef(false);
  const entityCountRef = useRef(0);
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const fetchEntities = useCallback(
    async (offset: number) => {
      const payload = await fetchReport(client, report, { ...filters, from, to, limit: ENTITY_CHUNK_SIZE, offset });
      return payload;
    },
    [client, report, filters, from, to],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchEntities(0)
      .then((payload) => {
        if (cancelled) return;
        const total = report.totalKey === undefined ? null : Number(payload.rows[0]?.[report.totalKey]);
        const more = total !== null && Number.isFinite(total) ? payload.rows.length < total : payload.rows.length === ENTITY_CHUNK_SIZE;
        entityCountRef.current = payload.rows.length;
        hasMoreRef.current = more;
        setEntities(payload.rows);
        setTotals(payload.totals ?? null);
        setUnattributed(payload.unattributed ?? null);
        setHasMore(more);
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        setEntities([]);
        setError(apiErrorMessage(caught, "Could not load this report."));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fetchEntities, report.totalKey]);

  const loadMoreEntities = useCallback(() => {
    if (loadingMoreRef.current || !hasMoreRef.current) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    fetchEntities(entityCountRef.current)
      .then((payload) => {
        if (!aliveRef.current) return;
        const total = report.totalKey === undefined ? null : Number(payload.rows[0]?.[report.totalKey]);
        entityCountRef.current += payload.rows.length;
        const more =
          payload.rows.length > 0 &&
          (total !== null && Number.isFinite(total) ? entityCountRef.current < total : payload.rows.length === ENTITY_CHUNK_SIZE);
        hasMoreRef.current = more;
        setHasMore(more);
        if (payload.rows.length > 0) setEntities((previous) => [...previous, ...payload.rows]);
      })
      .catch((caught: unknown) => {
        if (!aliveRef.current) return;
        hasMoreRef.current = false;
        setHasMore(false);
        setError(apiErrorMessage(caught, "Could not load more rows."));
      })
      .finally(() => {
        loadingMoreRef.current = false;
        if (aliveRef.current) setLoadingMore(false);
      });
  }, [fetchEntities, report.totalKey]);

  // ---- INSIDE: each entity's periods ----
  const [blocks, setBlocks] = useState<Readonly<Record<string, Block>>>({});
  const blocksRef = useRef<Readonly<Record<string, Block>>>({});
  blocksRef.current = blocks;
  const requestedRef = useRef(new Set<string>());
  /** Ids whose next chunk is on its way — a ref, because scroll events arrive faster than a render can record it. */
  const moreRef = useRef(new Set<string>());
  const activeRef = useRef(0);
  const queueRef = useRef<Array<() => Promise<void>>>([]);

  const patchBlocks = useCallback((ids: readonly string[], patch: (block: Block) => Block) => {
    setBlocks((previous) => {
      const next = { ...previous };
      for (const id of ids) next[id] = patch(previous[id] ?? EMPTY_BLOCK);
      return next;
    });
  }, []);

  const drainQueue = useCallback(() => {
    while (activeRef.current < MAX_PERIOD_REQUESTS && queueRef.current.length > 0) {
      const task = queueRef.current.shift();
      if (task === undefined) break;
      activeRef.current += 1;
      void task().finally(() => {
        activeRef.current -= 1;
        drainQueue();
      });
    }
  }, []);

  const breakdownQuery = useMemo(
    () => ({
      from,
      to,
      breakdown: mode,
      ...(filters.hierarchy === undefined ? {} : { hierarchy: filters.hierarchy }),
      ...(filters.campaignType === undefined ? {} : { campaignType: filters.campaignType }),
      ...(filters.campaignId === undefined ? {} : { campaignId: filters.campaignId }),
      ...(filters.adsetId === undefined ? {} : { adsetId: filters.adsetId }),
      ...(filters.adgroupId === undefined ? {} : { adgroupId: filters.adgroupId }),
    }),
    [from, to, mode, filters],
  );

  /** The first chunk of periods for every id not asked for yet — one request for the lot. */
  const fetchFirstChunks = useCallback(
    (ids: readonly string[]) => {
      const fresh = ids.filter((id) => id !== "" && !requestedRef.current.has(id));
      if (fresh.length === 0) return;
      for (const id of fresh) requestedRef.current.add(id);
      patchBlocks(fresh, (block) => ({ ...block, loading: true, failed: false }));
      queueRef.current.push(async () => {
        try {
          const payload = await fetchPeriodBreakdown(client, report, { ...breakdownQuery, ids: fresh, periodOffset: 0 });
          if (!aliveRef.current) return;
          setBlocks((previous) => {
            const next = { ...previous };
            for (const id of fresh) {
              next[id] = { periods: payload.breakdowns[id] ?? [], loading: false, loadingMore: false, hasMore: payload.hasMore, failed: false };
            }
            return next;
          });
        } catch (caught) {
          void caught;
          if (!aliveRef.current) return;
          // One block failing must not blank the table being read: it shows a retry instead.
          for (const id of fresh) requestedRef.current.delete(id);
          patchBlocks(fresh, (block) => ({ ...block, loading: false, failed: true }));
        }
      });
      drainQueue();
    },
    [client, report, breakdownQuery, patchBlocks, drainQueue],
  );

  /** The next chunk of one entity's periods. */
  const fetchMorePeriods = useCallback(
    (id: string) => {
      const block = blocksRef.current[id];
      if (block === undefined || block.loading || !block.hasMore || block.periods.length === 0 || moreRef.current.has(id)) return;
      moreRef.current.add(id);
      const offset = block.periods.length;
      patchBlocks([id], (current) => ({ ...current, loadingMore: true }));
      queueRef.current.push(async () => {
        try {
          const payload = await fetchPeriodBreakdown(client, report, { ...breakdownQuery, ids: [id], periodOffset: offset });
          if (!aliveRef.current) return;
          patchBlocks([id], (current) => ({
            periods: [...current.periods, ...(payload.breakdowns[id] ?? [])],
            loading: false,
            loadingMore: false,
            hasMore: payload.hasMore,
            failed: false,
          }));
        } catch (caught) {
          void caught;
          if (!aliveRef.current) return;
          // The block keeps what it has and stops asking for more.
          patchBlocks([id], (current) => ({ ...current, loadingMore: false, hasMore: false }));
        } finally {
          moreRef.current.delete(id);
        }
      });
      drainQueue();
    },
    [client, report, breakdownQuery, patchBlocks, drainQueue],
  );

  const retryBlock = useCallback(
    (id: string) => {
      requestedRef.current.delete(id);
      fetchFirstChunks([id]);
    },
    [fetchFirstChunks],
  );

  const outerRef = useRef<HTMLDivElement | null>(null);
  const rowRefs = useRef<Record<string, HTMLTableRowElement | null>>({});
  const sentinelRef = useRef<HTMLTableRowElement | null>(null);

  const idOf = useCallback((row: ReportRow): string => String(row[entityKey] ?? ""), [entityKey]);

  // The first chunk of entities is on screen as soon as it exists; ask for its periods at once.
  useEffect(() => {
    fetchFirstChunks(entities.slice(0, ENTITY_CHUNK_SIZE).map(idOf));
  }, [entities, fetchFirstChunks, idOf]);

  // Later entities ask for theirs when they scroll into view. (Without IntersectionObserver — old browsers, jsdom — ask for all.)
  useEffect(() => {
    const outer = outerRef.current;
    if (outer === null || entities.length === 0) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      fetchFirstChunks(entities.map(idOf));
      return undefined;
    }
    const observer = new IntersectionObserver(
      (observed) => {
        const visible: string[] = [];
        for (const entry of observed) {
          const id = entry.target.getAttribute("data-entity");
          if (entry.isIntersecting && id !== null) visible.push(id);
        }
        if (visible.length > 0) fetchFirstChunks(visible);
      },
      { root: outer, threshold: 0.05 },
    );
    for (const row of entities) {
      const element = rowRefs.current[idOf(row)];
      if (element) observer.observe(element);
    }
    return () => {
      observer.disconnect();
    };
  }, [entities, fetchFirstChunks, idOf]);

  // More entities when the bottom of the list comes into view — also covers a first chunk too short to scroll at all.
  useEffect(() => {
    const outer = outerRef.current;
    const sentinel = sentinelRef.current;
    if (outer === null || sentinel === null || !hasMore || typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver(
      (observed) => {
        if (observed.some((entry) => entry.isIntersecting)) loadMoreEntities();
      },
      { root: outer, rootMargin: "0px 0px 160px 0px" },
    );
    observer.observe(sentinel);
    return () => {
      observer.disconnect();
    };
  }, [hasMore, entities.length, loadMoreEntities]);

  const handleOuterScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const element = event.currentTarget;
      if (element.scrollHeight - element.scrollTop - element.clientHeight < 150) loadMoreEntities();
    },
    [loadMoreEntities],
  );

  // ---- the footer ----
  const totalRows: readonly ReportRow[] = [
    ...(totals == null ? entities : [totals]),
    ...(hasUnattributed(unattributed) ? [unattributed as unknown as ReportRow] : []),
  ];
  const footerTotal = (column: ReportColumn): number | null =>
    column.total?.kind === "distinct" || (column.total?.kind === "ratio" && column.total.totalsOnly === true)
      ? columnTotal(column, totals == null ? entities : [totals])
      : columnTotal(column, totalRows);

  const modeWord = mode === "day" ? "Day" : "Week";
  const labelHeader = `${nameColumn?.label ?? "Name"} & ${modeWord} Breakdown`;

  if (target === undefined || nameColumn === undefined) {
    return <p className="text-destructive p-4 text-sm">This report has no day / week view.</p>;
  }

  return (
    <div>
      {error !== null && (
        <p className="text-destructive px-4 pt-3 text-sm" role="alert">
          {error}
        </p>
      )}

      <div ref={outerRef} onScroll={handleOuterScroll} className="max-h-[min(65svh,44rem)] overflow-auto">
        <table className="table-fixed border-separate border-spacing-0 text-sm" style={{ width: `${String(totalWidth)}px`, minWidth: "100%" }}>
          <colgroup>
            <col style={{ width: `${String(widthAt(0))}px` }} />
            {metricColumns.map((column, index) => (
              <col key={column.key} style={{ width: `${String(widthAt(index + 1))}px` }} />
            ))}
          </colgroup>

          <thead className="sticky top-0 z-30">
            <tr>
              <th
                rowSpan={2}
                className="row-head text-foreground sticky left-0 z-40 h-11 border-b border-r border-border px-3 text-left align-middle text-[13px] font-semibold whitespace-nowrap select-none"
              >
                {labelHeader}
                <TableResizeHandle onResize={(event) => startResize(0, event)} />
              </th>
              {bands.map((band, index) => (
                <th
                  key={`${band.label ?? "plain"}:${String(index)}`}
                  colSpan={band.span}
                  className={cn(
                    "row-head text-foreground h-11 border-b border-r px-3 text-center text-[13px] font-semibold select-none",
                    index > 0 && "border-l",
                  )}
                >
                  {band.label}
                </th>
              ))}
            </tr>
            <tr>
              {metricColumns.map((column, index) => (
                <th
                  key={column.key}
                  className={cn(
                    "text-muted-foreground row-head relative border-b border-r py-2.5 pr-4 pl-3 text-right text-[13px] font-medium whitespace-nowrap select-none",
                    bandStarts.has(index) && index > 0 && "border-l",
                  )}
                >
                  {column.label}
                  <TableResizeHandle onResize={(event) => startResize(index + 1, event)} />
                </th>
              ))}
            </tr>
          </thead>

          {loading ? (
            <tbody aria-busy="true" aria-label="Loading report">
              {Array.from({ length: 6 }, (_, rowIndex) => (
                <tr key={rowIndex} className={rowIndex % 2 === 1 ? "bg-muted/15" : "bg-card"}>
                  <td className="bg-card sticky left-0 z-20 border-r border-b px-3 py-3">
                    <Skeleton className="h-4 w-48" />
                  </td>
                  {metricColumns.map((column) => (
                    <td key={column.key} className="border-b px-3 py-3">
                      <Skeleton className="ml-auto h-4 w-16" />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          ) : (
            <tbody>
              {entities.length === 0 && (
                <tr>
                  <td colSpan={metricColumns.length + 1} className="text-muted-foreground p-8 text-center text-sm italic">
                    Nothing to show in this date range.
                  </td>
                </tr>
              )}

              {entities.map((row) => {
                const id = idOf(row);
                const block = blocks[id] ?? EMPTY_BLOCK;
                return (
                  <EntityBlock
                    key={id}
                    id={id}
                    row={row}
                    block={block}
                    nameColumn={nameColumn}
                    labelKey={labelKey}
                    metricColumns={metricColumns}
                    metricWidths={metricWidths}
                    metricTotalWidth={metricTotalWidth}
                    bandStarts={bandStarts}
                    outerRef={outerRef}
                    rowRef={(element) => {
                      rowRefs.current[id] = element;
                    }}
                    onNeedMore={() => fetchMorePeriods(id)}
                    onRetry={() => retryBlock(id)}
                  />
                );
              })}

              {(hasMore || loadingMore) && (
                <tr ref={sentinelRef}>
                  <td colSpan={metricColumns.length + 1} className="text-muted-foreground bg-muted/20 border-b px-4 py-3 text-center text-xs">
                    <span className="inline-flex items-center gap-2">
                      <span className="border-primary size-3.5 animate-spin rounded-full border-2 border-t-transparent" />
                      {loadingMore ? "Loading more rows…" : "Scroll to load more rows"}
                    </span>
                  </td>
                </tr>
              )}
            </tbody>
          )}

          {!loading && entities.length > 0 && (
            <tfoot className="sticky bottom-0 z-30 font-semibold">
              <tr className="bg-muted/95 backdrop-blur-md">
                <td className="bg-muted/95 border-border sticky bottom-0 left-0 z-40 border-t-2 border-r px-3 py-2.5 text-[13px] shadow-[0_-2px_6px_rgba(0,0,0,0.06)] backdrop-blur-md">
                  Overall Totals
                </td>
                {metricColumns.map((column, index) => {
                  const total = footerTotal(column);
                  return (
                    <td
                      key={column.key}
                      className={cn(
                        "bg-muted/95 border-border text-foreground sticky bottom-0 z-30 border-t-2 py-2.5 pr-4 pl-3 text-right text-[13px] font-semibold whitespace-nowrap tabular-nums shadow-[0_-2px_6px_rgba(0,0,0,0.06)] backdrop-blur-md",
                        bandStarts.has(index) && index > 0 && "border-l",
                      )}
                    >
                      {total === null ? (
                        <span title={column.noTotalReason ?? "No total for this column."} className="cursor-help">
                          —
                        </span>
                      ) : (
                        cellText(column.kind, total, column.key)
                      )}
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <div className="text-muted-foreground flex items-center justify-between px-3 py-2 text-xs">
        <span>
          Showing {entities.length.toLocaleString("en-US")} {entities.length === 1 ? "row" : "rows"}, by {modeWord.toLowerCase()}. Order follows the
          consolidated report&apos;s sort.
        </span>
        {hasMore && <span className="italic">Scroll down the table to load more rows</span>}
      </div>
    </div>
  );
}

/** A figure for a period with no row in the answer: nothing happened, so a count or amount is 0 and a ratio is unknown. */
function periodValue(column: ReportColumn, period: PeriodRow): string | number | null {
  const value = period[column.key];
  if (value !== undefined && value !== null) return value;
  return column.kind === "count" || column.kind === "money" ? 0 : null;
}

function EntityBlock({
  id,
  row,
  block,
  nameColumn,
  labelKey,
  metricColumns,
  metricWidths,
  metricTotalWidth,
  bandStarts,
  outerRef,
  rowRef,
  onNeedMore,
  onRetry,
}: {
  readonly id: string;
  readonly row: ReportRow;
  readonly block: Block;
  readonly nameColumn: ReportColumn;
  readonly labelKey: string;
  readonly metricColumns: readonly ReportColumn[];
  readonly metricWidths: readonly number[];
  readonly metricTotalWidth: number;
  readonly bandStarts: ReadonlySet<number>;
  readonly outerRef: React.RefObject<HTMLDivElement | null>;
  readonly rowRef: (element: HTMLTableRowElement | null) => void;
  readonly onNeedMore: () => void;
  readonly onRetry: () => void;
}): React.JSX.Element {
  const leftRef = useRef<HTMLDivElement | null>(null);
  const rightRef = useRef<HTMLDivElement | null>(null);

  // The metric half of a block never scrolls on its own: the wheel belongs to the page (vertical) and to the table (horizontal),
  // and the block's periods are scrolled from the label column, which drives this half.
  useEffect(() => {
    const element = rightRef.current;
    const outer = outerRef.current;
    if (element === null || outer === null) return undefined;
    const onWheel = (event: WheelEvent): void => {
      event.preventDefault();
      if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) outer.scrollLeft += event.deltaX;
      else if (event.shiftKey) outer.scrollLeft += event.deltaY;
      else outer.scrollTop += event.deltaY;
    };
    element.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      element.removeEventListener("wheel", onWheel);
    };
  }, [outerRef, block.periods.length]);

  const onLeftScroll = (event: React.UIEvent<HTMLDivElement>): void => {
    const source = event.currentTarget;
    const follower = rightRef.current;
    if (follower !== null && follower.scrollTop !== source.scrollTop) follower.scrollTop = source.scrollTop;
    if (source.scrollHeight - source.scrollTop - source.clientHeight < 45) onNeedMore();
  };

  const name = String(row[labelKey] ?? id);
  const status = row["status"];
  const initialLoading = block.loading && block.periods.length === 0;

  return (
    <>
      <tr ref={rowRef} data-entity={id} className="bg-muted/30 hover:bg-muted/50 border-b font-semibold transition-colors">
        <td className="bg-card sticky left-0 z-20 max-w-0 border-r border-b px-3 py-2.5">
          <div className="flex max-w-full min-w-0 items-center justify-between gap-2">
            <span className="text-foreground flex min-w-0 items-center gap-2 text-[13px] font-semibold" title={name}>
              {nameColumn.brandMark === true ? <SourceMark value={name} /> : null}
              <span className="truncate">{name}</span>
              {status === "active" || status === "inactive" ? (
                <span className="text-muted-foreground shrink-0 text-[10px] font-medium">{status === "active" ? "Active" : "Inactive"}</span>
              ) : null}
            </span>
            <span className="bg-primary/10 text-primary border-primary/20 shrink-0 rounded border px-1.5 py-0.5 text-[10px] font-semibold">
              Grand Total
            </span>
          </div>
        </td>
        {metricColumns.map((column, index) => (
          <td
            key={column.key}
            className={cn(
              "border-b py-2.5 pr-4 pl-3 text-right text-[13px] whitespace-nowrap tabular-nums",
              bandStarts.has(index) && index > 0 && "border-l",
            )}
          >
            {cellText(column.kind, row[column.key], column.key)}
          </td>
        ))}
      </tr>

      {initialLoading ? (
        <tr>
          <td className="bg-card sticky left-0 z-20 border-r border-b p-0 align-top">
            <div className="flex flex-col justify-around overflow-hidden px-4 py-3" style={{ height: BLOCK_MAX_HEIGHT }}>
              <Skeleton className="h-4 w-44" />
              <Skeleton className="h-4 w-52" />
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-4 w-48" />
            </div>
          </td>
          <td colSpan={metricColumns.length} className="border-b p-0 align-top">
            <div className="flex flex-col justify-around overflow-hidden px-4 py-3" style={{ height: BLOCK_MAX_HEIGHT }}>
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
            </div>
          </td>
        </tr>
      ) : block.failed ? (
        <tr>
          <td colSpan={metricColumns.length + 1} className="text-muted-foreground border-b px-8 py-2 text-xs">
            Could not load the breakdown for this row.{" "}
            <button type="button" className="text-primary hover:underline" onClick={onRetry}>
              Try again
            </button>
          </td>
        </tr>
      ) : block.periods.length > 0 ? (
        <tr>
          <td className="bg-card sticky left-0 z-20 max-w-0 border-r border-b p-0 align-top">
            <div
              ref={leftRef}
              onScroll={onLeftScroll}
              className="overflow-x-hidden overflow-y-auto overscroll-contain [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              style={{ maxHeight: BLOCK_MAX_HEIGHT }}
            >
              <table className="w-full table-fixed border-collapse">
                <tbody>
                  {block.periods.map((period) => (
                    <tr key={period.period_num} className="hover:bg-muted/20 border-b transition-colors" style={{ height: BLOCK_ROW_HEIGHT }}>
                      <td className="max-w-0 py-1.5 pr-3 pl-8 text-[13px]">
                        <div className="flex max-w-full min-w-0 items-center gap-1.5">
                          <span className="text-foreground shrink-0 font-medium">{period.period_label}</span>
                          <span className="text-muted-foreground truncate text-xs">
                            ({period.period_start === period.period_end ? period.period_start : `${period.period_start} to ${period.period_end}`})
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {block.loadingMore && (
                    <tr className="border-b" style={{ height: BLOCK_ROW_HEIGHT }}>
                      <td className="text-muted-foreground py-1.5 pr-3 pl-8 text-[12px] italic">Loading more…</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </td>

          <td colSpan={metricColumns.length} className="border-b p-0 align-top">
            <div ref={rightRef} className="overflow-hidden" style={{ maxHeight: BLOCK_MAX_HEIGHT }}>
              <table className="table-fixed border-separate border-spacing-0 text-sm" style={{ width: `${String(metricTotalWidth)}px`, minWidth: "100%" }}>
                <colgroup>
                  {metricWidths.map((width, index) => (
                    <col key={metricColumns[index]?.key ?? index} style={{ width: `${String(width)}px` }} />
                  ))}
                </colgroup>
                <tbody>
                  {block.periods.map((period) => (
                    <tr key={period.period_num} className="hover:bg-muted/20 border-b transition-colors" style={{ height: BLOCK_ROW_HEIGHT }}>
                      {metricColumns.map((column, index) => (
                        <td
                          key={column.key}
                          className={cn(
                            "text-muted-foreground py-1.5 pr-4 pl-3 text-right text-[13px] whitespace-nowrap tabular-nums",
                            bandStarts.has(index) && index > 0 && "border-l",
                          )}
                        >
                          {cellText(column.kind, periodValue(column, period), column.key)}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {block.loadingMore && (
                    <tr className="border-b" style={{ height: BLOCK_ROW_HEIGHT }}>
                      {metricColumns.map((column) => (
                        <td key={column.key} className="py-1.5 pr-4 pl-3 text-right">
                          <Skeleton className="ml-auto h-3 w-12" />
                        </td>
                      ))}
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}

function SourceMark({ value }: { readonly value: string }): React.JSX.Element {
  const { Icon, color } = sourceMark(value);
  return Icon === null ? (
    <span aria-hidden="true" className="bg-muted-foreground/40 size-2 shrink-0 rounded-full" />
  ) : (
    <Icon aria-hidden="true" className="size-4 shrink-0" style={{ color }} />
  );
}
